import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSEO } from '@/composables/useSEO'
import LoginView from '../views/LoginView.vue'
import RegisterView from '../views/RegisterView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/CustomerCatalogView.vue')
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
      meta: { requiresGuest: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordView,
      meta: { requiresGuest: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/pos',
      name: 'pos',
      component: () => import('../views/POSView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/catalog',
      name: 'catalog',
      component: () => import('../views/CustomerCatalogView.vue')
    },
    {
      path: '/inventory',
      name: 'inventory',
      component: () => import('../views/InventoryView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/products',
      name: 'products',
      component: () => import('../views/ProductsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/categories',
      name: 'categories',
      component: () => import('../views/CategoriesView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../views/ReportsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/OrdersView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/payments',
      name: 'payments',
      component: () => import('../views/PaymentsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/orders/:id',
      name: 'order-details',
      component: () => import('../views/OrderDetailsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('../views/SettingsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/CustomersView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor', 'sales_vendor'] }
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/NotificationsView.vue'),
      meta: { requiresAuth: true, roles: ['admin_vendor'] }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: { requiresAuth: false }
    },
    // SEO-friendly product routes
    {
      path: '/products/:id',
      name: 'product-detail',
      component: () => import('../views/ProductDetailView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/category/:category',
      name: 'category-products',
      component: () => import('../views/CategoryProductsView.vue'),
      meta: { requiresAuth: false }
    },
    // Redirects for SEO
    {
      path: '/signin',
      redirect: '/login'
    },
    {
      path: '/signup',
      redirect: '/register'
    },
    {
      path: '/shop',
      redirect: '/catalog'
    },
    {
      path: '/store',
      redirect: '/catalog'
    },
    // 404 page
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue')
    },
  ],
})

// Navigation guards
let isInitializing = false

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Update SEO based on route
  if (typeof window !== 'undefined') {
    const { updateSEO } = useSEO(to.name as string)
    updateSEO()
  }

  // Wait for auth to be initialized (prevent multiple calls)
  if (authStore.token && !authStore.user && !authStore.loading && !isInitializing) {
    isInitializing = true
    try {
      await authStore.fetchUser()
    } catch (error) {
      console.error('Router guard fetchUser error:', error)
    } finally {
      isInitializing = false
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    if (authStore.isAdminVendor) {
      next('/dashboard')
    } else if (authStore.isSalesVendor) {
      next('/pos')
    } else if (authStore.isCustomer) {
      next('/catalog')
    }
    return
  }

  // Check role-based access
  if (to.meta.roles && authStore.user && Array.isArray(to.meta.roles) && !to.meta.roles.includes(authStore.user.role)) {
    next('/unauthorized')
    return
  }

  next()
})

export default router
